import { isTyreResult } from '#app/store/middlewares/configurationRequests/checks/runTyreCheck'
import { isDvsResult } from '#store/middlewares/configurationRequests/checks/runDvsCheck'
import { isOasResult } from '#store/middlewares/configurationRequests/checks/runOasCheck'
import { isWeightResult } from '#store/middlewares/configurationRequests/checks/runWeightCheck'
import type { CheckResult } from '#types/ConfigurationCheck'

export function deriveFinalStatus(checks: CheckResult[], fallbackStatus: CheckResult['status'] = 'success') {
  let finalState = fallbackStatus

  for (const check of checks) {
    if (check.status === 'failure') {
      finalState = 'failure'
      break
    }
    if (check.status === 'aborted') {
      finalState = 'aborted'
      break
    }
    if (check.status === 'progress') {
      finalState = 'progress'
      break
    }
    if (check.status === 'queued') {
      finalState = 'queued'
    }
  }

  return finalState
}

export function isFinished(checkStatus: CheckResult['status'] | undefined) {
  return checkStatus === 'success' || checkStatus === 'failure' || checkStatus === 'aborted'
}

export function canRetry(checkStatus: CheckResult['status']) {
  return checkStatus !== 'progress' && checkStatus !== 'queued' && checkStatus !== 'success'
}

export function isCheckDisabled(check: CheckResult) {
  return check.status === 'success' && check.data?.disabled
}

export function getCheckWarningCount(check: CheckResult) {
  let warningCount = 0

  if (isOasResult(check) && check.data) {
    warningCount += check.data.oasResult?.warnings && check.data.oasResult.warnings.length > 0 ? check.data.oasResult.warnings.length : 0
  }

  if (check.data?.signalStatus === 'Yellow') {
    warningCount = Math.max(1, warningCount)
  }

  return warningCount
}

export function getCheckErrorCount(check: CheckResult) {
  let errorCount = 0
  if (isOasResult(check) && check.data) {
    errorCount += check.data.oasResult?.errors && check.data.oasResult.errors.length > 0 ? check.data.oasResult.errors.length : 0
  }

  if (check.data && 'messages' in check.data && Array.isArray(check.data?.messages)) {
    errorCount += check.data.messages.length
  }

  if ((isDvsResult(check) || isWeightResult(check)) && check.data?.hasErrors) {
    errorCount = Math.max(1, errorCount)
  }

  if (check.data?.isConnectionError) {
    errorCount = Math.max(1, errorCount)
  }

  if (check.data?.signalStatus === 'Red') {
    errorCount = Math.max(1, errorCount)
  }

  // tyre checks claim to be ok, when sub checks have failed
  if (isTyreResult(check)) {
    errorCount += check.data?.content?.tyreDetails?.filter((x) => x.status === 'FAILED').length ?? 0
  }

  return errorCount
}

export function isFullCheckResult(result: Partial<CheckResult>): result is CheckResult {
  return result.id !== undefined && result.status !== undefined && result.startTime !== undefined
}

export function calculateTotalProgress(checks: CheckResult[]) {
  const finishedCount = checks.filter((x) => isFinished(x.status)).length
  const doneProgress = finishedCount / checks.length
  const ongoingTasks = checks.filter((x) => x.status === 'progress')
  const estimatedProgress = ongoingTasks.map((x) => {
    if (!x.estimatedDuration) {
      return 0
    }
    const timePassed = Date.now() - x.startTime
    return Math.min(0.99, timePassed / x.estimatedDuration)
  })
  const ongoingProgress = estimatedProgress.reduce((a, b) => a + b, 0) / checks.length
  return ongoingProgress + doneProgress
}
